import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { TeeTimeProvider } from './contexts/TeeTimeContext';
import { useTeeTime } from './hooks/useTeeTime';
import DateSelector from './components/DateSelector';
import TeeTimeList from './components/TeeTimeList';
import BookingForm from './components/BookingForm';
import UserBookings from './components/UserBookings';
import GolfCourseGallery from './components/GolfCourseGallery';
import { GolfCourse } from './types';
import './App.css';

// Page imports
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import PartnersPage from './pages/PartnersPage';
import GolfTipsPage from './pages/GolfTipsPage';
import CourseReviewsPage from './pages/CourseReviewsPage';
import EquipmentGuidePage from './pages/EquipmentGuidePage';
import HelpCenterPage from './pages/HelpCenterPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import BlogPostPage from './pages/BlogPostPage';
import CourseReviewPage from './pages/CourseReviewPage';

// Basic style constants
const styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh',
  },
  contentArea: {
    flex: 1,
    padding: '2rem 1rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  twoColumnLayout: {
    display: 'flex',
    gap: '2rem',
    width: '100%',
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
  },
  sidebarColumn: {
    width: '350px',
    minWidth: '350px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
};

/**
 * Main content of the application
 * Separated to access the TeeTime context
 */
const AppContent: React.FC = () => {
  const teeTimeContext = useTeeTime();
  const { selectedCourse, setSelectedCourse } = teeTimeContext as { 
    selectedCourse: GolfCourse | null, 
    setSelectedCourse: (course: GolfCourse | null) => void 
  };
  const [activeTab, setActiveTab] = useState<'book' | 'mybookings'>('book');
  
  /**
   * Handles selecting a golf course
   * @param course The selected course
   */
  const handleSelectCourse = (course: GolfCourse) => {
    setSelectedCourse(course);
  };
  
  /**
   * Renders the appropriate content based on active tab and selected course
   */
  const renderContent = () => {
    if (activeTab === 'mybookings') {
      return <UserBookings />;
    }
    
    if (!selectedCourse) {
      return (
        <GolfCourseGallery 
          onSelectCourse={handleSelectCourse}
          selectedCourseId={undefined}
        />
      );
    }
    
    return (
      <div style={styles.twoColumnLayout}>
        <div style={styles.mainColumn}>
          <DateSelector />
          <TeeTimeList />
          <div className="course-selection-link">
            <button onClick={() => setSelectedCourse(null)} className="change-course-button">
              ← Change Golf Course
            </button>
          </div>
        </div>
        <div style={styles.sidebarColumn}>
          <BookingForm />
        </div>
      </div>
    );
  };
  
  const location = useLocation();
  const navigate = useNavigate();
  
  return (
    <div style={styles.mainContainer} className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="logo-container">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <img 
                src="/images/golfscape-logo.png" 
                alt="GolfScape Tee Off Logo" 
                style={{ 
                  maxWidth: '280px', 
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
            </Link>
          </div>
          
          <nav className="main-nav">
            <button 
              className={activeTab === 'book' ? 'active' : ''}
              onClick={() => {
                setActiveTab('book');
                if (location.pathname !== '/') navigate('/');
              }}
            >
              Book Tee Time
            </button>
            <button 
              className={activeTab === 'mybookings' ? 'active' : ''}
              onClick={() => {
                setActiveTab('mybookings');
                if (location.pathname !== '/') navigate('/');
              }}
            >
              My Bookings
            </button>
          </nav>
        </div>
        
        {selectedCourse && (
          <div className="selected-course-banner" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${selectedCourse.imageUrl})` }}>
            <div className="course-info">
              <h2>{selectedCourse.name}</h2>
              <p className="course-location">{selectedCourse.location}</p>
              <div className="course-quick-info">
                <span className="course-holes">{selectedCourse.holeCount} holes</span>
                <span className="course-difficulty">{selectedCourse.difficulty}</span>
                <div className="course-rating">
                  {Array(5).fill('★').map((star, i) => (
                    <span key={i} className={i < Math.floor(selectedCourse.rating) ? 'filled' : ''}>{star}</span>
                  ))}
                  <span className="rating-number">{selectedCourse.rating.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      
      <main style={styles.contentArea}>
        {renderContent()}
      </main>
      
      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <h2>GolfScape</h2>
              <p>Making golf accessible since 2023</p>
            </Link>
          </div>
          
          <div className="footer-links">
            <div className="footer-column">
              <h3>Company</h3>
              <ul>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/careers">Careers</Link></li>
                <li><Link to="/partners">Partners</Link></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>Resources</h3>
              <ul>
                <li><Link to="/golf-tips">Golf Tips</Link></li>
                <li><Link to="/course-reviews">Course Reviews</Link></li>
                <li><Link to="/equipment-guide">Equipment Guide</Link></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>Support</h3>
              <ul>
                <li><Link to="/help">Help Center</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} GolfScape. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

/**
 * Main App component for the Golf Tee Time Booking application
 * @returns React component
 */
const App: React.FC = () => {
  return (
    <Router>
      <TeeTimeProvider>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/golf-tips" element={<GolfTipsPage />} />
          <Route path="/golf-tips/:slug" element={<BlogPostPage />} />
          <Route path="/course-reviews" element={<CourseReviewsPage />} />
          <Route path="/course-reviews/:slug" element={<CourseReviewPage />} />
          <Route path="/equipment-guide" element={<EquipmentGuidePage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes>
      </TeeTimeProvider>
    </Router>
  );
};

export default App;
